import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { kes } from '@/lib/format';
import { NiaMark } from '@/components/NiaLogo';

/**
 * Post-login welcome / setup flow.
 *
 * Phase V3 (Recipient app retired): this screen is now sender-only.
 * Senders walk a 3-step setup (first name -> monthly support amount ->
 * support buffer preview). Agents have their own profile-completion flow
 * at /agent/profile so they never hit this screen.
 */
export default function WelcomeSetup() {
    const nav = useNavigate();
    const { user, refreshUser } = useAuth();
    const totalSteps = 3;
    const [step, setStep] = useState(0);
    const [firstName, setFirstName] = useState(deriveFirstName(user));
    const [amount, setAmount] = useState(user?.monthly_support_declared_kes ? String(user.monthly_support_declared_kes) : '');
    const [busy, setBusy] = useState(false);

    const dashboard = '/sender/home';
    // After onboarding, route to the first-run disclosure screen the first time;
    // FirstRunDisclosure stores `nia_disclosure_v1` once acknowledged.
    const postSetupRoute = () => {
        try {
            if (localStorage.getItem('nia_disclosure_v1') !== '1') return '/welcome';
        } catch (e) { console.debug('localStorage unavailable; skipping disclosure check', e); }
        return dashboard;
    };

    const submitFinal = async () => {
        setBusy(true);
        try {
            const payload = {
                first_name: firstName.trim(),
                monthly_support_kes: parseInt(amount || '0', 10),
            };
            await api.completeOnboarding(payload);
            await refreshUser();
            toast.success(`Welcome to Nia, ${firstName.trim()}`);
            nav(postSetupRoute(), { replace: true });
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not save');
        } finally {
            setBusy(false);
        }
    };

    const goNext = async () => {
        if (step === 0) {
            if (!firstName.trim()) return toast.error('Please enter your first name');
            setStep(1);
            return;
        }
        if (step === 1) {
            const a = parseInt(amount || '0', 10);
            if (a < 1000) return toast.error('Enter at least KSh 1,000');
            setStep(2);
            return;
        }
        await submitFinal();
    };

    const goBack = () => { if (step > 0) setStep(step - 1); };

    const buffer = Math.floor((parseInt(amount || '0', 10) || 0) * 0.75); // internal — not surfaced

    return (
        <div className="nia-page">
            <div className="mobile-shell">
                <header style={{ padding: '22px 22px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <NiaMark size={36} />
                    <Steps count={totalSteps} active={Math.min(step, totalSteps - 1)} />
                </header>

                <div className="nia-page-content" style={{ paddingTop: 18 }}>
                    {step === 0 && (
                        <div className="fade-in">
                            <div style={{ color: 'var(--nia-subtext)', fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Welcome</div>
                            <h1 className="font-display" style={{ fontSize: 30, fontWeight: 800, margin: '6px 0 8px', letterSpacing: '-0.03em' }}>What should we call you?</h1>

                            <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--nia-subtext)', display: 'block', marginBottom: 8, marginTop: 24 }}>First name</label>
                            <input
                                autoFocus
                                className="nia-input"
                                placeholder="e.g. Alex"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && goNext()}
                                maxLength={40}
                                data-testid="setup-firstname-input"
                            />
                        </div>
                    )}

                    {step === 1 && (
                        <div className="fade-in">
                            <div style={{ color: 'var(--nia-subtext)', fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Setup</div>
                            <h1 className="font-display" style={{ fontSize: 28, fontWeight: 800, margin: '6px 0 8px', letterSpacing: '-0.03em' }}>How much do you send home each month?</h1>

                            <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--nia-subtext)', display: 'block', marginBottom: 8, marginTop: 24 }}>Monthly amount (KSh)</label>
                            <div style={{ position: 'relative' }}>
                                <span style={{ position: 'absolute', left: 18, top: 16, color: 'var(--nia-subtext)', fontWeight: 700, fontSize: 16 }}>KSh</span>
                                <input
                                    autoFocus
                                    className="nia-input"
                                    style={{ paddingLeft: 64, fontWeight: 800, fontSize: 18 }}
                                    placeholder="25,000"
                                    inputMode="numeric"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ''))}
                                    onKeyDown={(e) => e.key === 'Enter' && goNext()}
                                    data-testid="setup-amount-input"
                                />
                            </div>
                            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                                {[10000, 25000, 50000, 100000, 250000].map((preset) => (
                                    <button key={preset} onClick={() => setAmount(String(preset))} style={{
                                        padding: '8px 14px', borderRadius: 999, border: '1px solid',
                                        borderColor: amount === String(preset) ? 'var(--nia-primary)' : 'var(--nia-border)',
                                        background: amount === String(preset) ? 'var(--nia-primary-tint)' : 'white',
                                        color: amount === String(preset) ? 'var(--nia-primary)' : 'var(--nia-text)',
                                        fontWeight: 700, fontSize: 13, cursor: 'pointer',
                                    }}>{kes(preset)}</button>
                                ))}
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="fade-in">
                            <div style={{ color: 'var(--nia-subtext)', fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>You&apos;re ready</div>
                            <h1 className="font-display" style={{ fontSize: 30, fontWeight: 800, margin: '6px 0 16px', letterSpacing: '-0.03em' }}>Your support buffer is ready.</h1>

                            <div className="nia-card-dark" style={{ padding: 22 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative', zIndex: 1 }}>
                                    <Sparkles size={16} color="var(--nia-gold)" />
                                    <div style={{ fontWeight: 700, fontSize: 14 }}>Planned monthly support</div>
                                </div>
                                <div className="font-display" style={{ fontSize: 44, fontWeight: 800, letterSpacing: '-0.04em', marginTop: 4, position: 'relative', zIndex: 1 }}>{kes(buffer)}</div>
                            </div>

                            <div className="nia-card" style={{ padding: 14, marginTop: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                <ShieldCheck size={16} color="var(--nia-primary)" style={{ flexShrink: 0, marginTop: 2 }} />
                                <div style={{ fontSize: 12, color: 'var(--nia-subtext)', lineHeight: 1.55 }}>
                                    <b style={{ color: 'var(--nia-text)' }}>A planning tool, not a bank.</b> Nia helps you coordinate and track family support. We never extend credit or move money.
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div style={{ padding: '12px 18px 24px', display: 'flex', gap: 10 }}>
                    {step > 0 && (
                        <button onClick={goBack} className="nia-btn-secondary" style={{ width: 'auto', padding: '13px 20px' }}>Back</button>
                    )}
                    <button
                        onClick={goNext}
                        disabled={busy}
                        className="nia-btn-primary"
                        style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                        data-testid="setup-continue"
                    >
                        {busy ? 'Saving…' : (step === totalSteps - 1) ? 'Continue to Nia' : 'Continue'}
                        {!busy && <ArrowRight size={16} />}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Steps({ count, active }) {
    return (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {Array.from({ length: count }).map((_, i) => (
                <span key={`step-${i}`} style={{
                    width: i === active ? 22 : 8, height: 8,
                    borderRadius: 999,
                    background: i <= active ? 'var(--nia-primary)' : 'var(--nia-border)',
                    transition: 'all 0.18s ease',
                }} />
            ))}
        </div>
    );
}

function deriveFirstName(user) {
    if (!user) return '';
    const name = user.name || '';
    if (!name || name.toLowerCase().includes('new member') || name.toLowerCase().startsWith('user')) return '';
    return name.split(' ')[0];
}
